import { useState, useRef } from 'react';
import { publicApi } from '../utils/api';
import { Play, Upload, Link as LinkIcon, Code, Copy, CheckCircle, Layers, Zap, Image, X, Plus, AlertTriangle, Shield, ShieldAlert, ShieldCheck, RefreshCw, Eye, Clock, Cpu } from 'lucide-react';
import toast from 'react-hot-toast';
import { Highlight, themes } from 'prism-react-renderer';

const ApiPlayground = () => {
  const [apiKey, setApiKey] = useState('');
  const [endpoint, setEndpoint] = useState('single'); // single, multi, urls, streaming
  const [inputType, setInputType] = useState('url'); // url or file
  const [imageUrl, setImageUrl] = useState('');
  const [imageUrls, setImageUrls] = useState(['']);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [streamingResults, setStreamingResults] = useState([]);
  const [language, setLanguage] = useState('curl');
  const [showRawResponse, setShowRawResponse] = useState(false);
  const fileInputRef = useRef(null);

  const endpoints = [
    { id: 'single', name: 'Single', icon: Image, desc: 'Analyze one image', color: 'blue' },
    { id: 'multi', name: 'Multi', icon: Layers, desc: 'Batch analyze (up to 10)', color: 'purple' },
    { id: 'urls', name: 'URLs', icon: LinkIcon, desc: 'Analyze from URLs', color: 'green' },
    { id: 'streaming', name: 'Streaming', icon: Zap, desc: 'Real-time streaming', color: 'orange' },
  ];

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      toast.error('Some files were not images and were skipped');
    }
    
    const maxFiles = endpoint === 'streaming' ? 100 : 10;
    if (imageFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      setSelectedFiles(imageFiles.slice(0, maxFiles));
    } else {
      setSelectedFiles(imageFiles);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const addUrlField = () => {
    if (imageUrls.length < 10) {
      setImageUrls([...imageUrls, '']);
    }
  };

  const removeUrlField = (index) => {
    if (imageUrls.length > 1) {
      setImageUrls(imageUrls.filter((_, i) => i !== index));
    }
  };

  const updateUrl = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  // Helper to get the decision from response data
  const getDecision = (data) => {
    if (!data) return null;
    
    // Handle error responses
    if (data.error) return 'error';
    if (!data.success && data.error) return 'error';
    
    // For single image response
    if (data.data?.final_decision) {
      return data.data.final_decision.toLowerCase() === 'nsfw' ? 'nsfw' : 'safe';
    }
    
    // For multi/urls response with predictions array
    if (data.data?.predictions) {
      const hasNsfw = data.data.predictions.some(p => 
        p.final_decision?.toLowerCase() === 'nsfw'
      );
      return hasNsfw ? 'nsfw' : 'safe';
    }
    
    // For direct response from model
    if (data.final_decision) {
      return data.final_decision.toLowerCase() === 'nsfw' ? 'nsfw' : 'safe';
    }
    
    return data.success ? 'safe' : 'error';
  };

  const handleAnalyze = async () => {
    if (!apiKey) {
      toast.error('Please enter your API key');
      return;
    }

    if (apiKey.length < 20) {
      toast.error('Invalid API key format. Please check your API key.');
      return;
    }

    setLoading(true);
    setResult(null);
    setStreamingResults([]);

    try {
      let response;

      switch (endpoint) {
        case 'single':
          if (inputType === 'url') {
            if (!imageUrl) {
              toast.error('Please enter an image URL');
              setLoading(false);
              return;
            }
            response = await publicApi.analyzeSingle({ url: imageUrl }, apiKey);
          } else {
            if (selectedFiles.length === 0) {
              toast.error('Please select an image file');
              setLoading(false);
              return;
            }
            response = await publicApi.analyzeSingle({ file: selectedFiles[0] }, apiKey);
          }
          setResult(response.data);
          break;

        case 'multi':
          if (selectedFiles.length === 0) {
            toast.error('Please select image files');
            setLoading(false);
            return;
          }
          if (selectedFiles.length > 10) {
            toast.error('Maximum 10 files allowed for multi endpoint');
            setLoading(false);
            return;
          }
          response = await publicApi.analyzeMulti(selectedFiles, apiKey);
          setResult(response.data);
          break;

        case 'urls':
          const validUrls = imageUrls.filter(url => url.trim());
          if (validUrls.length === 0) {
            toast.error('Please enter at least one image URL');
            setLoading(false);
            return;
          }
          if (validUrls.length > 10) {
            toast.error('Maximum 10 URLs allowed');
            setLoading(false);
            return;
          }
          response = await publicApi.analyzeUrls(validUrls, apiKey);
          setResult(response.data);
          break;

        case 'streaming':
          if (selectedFiles.length === 0) {
            toast.error('Please select image files');
            setLoading(false);
            return;
          }
          if (selectedFiles.length > 100) {
            toast.error('Maximum 100 files allowed for streaming endpoint');
            setLoading(false);
            return;
          }
          try {
            await publicApi.analyzeStreaming(selectedFiles, apiKey, (data) => {
              setStreamingResults(prev => [...prev, data]);
            });
          } catch (streamError) {
            console.error('Streaming error:', streamError);
            setResult({ 
              success: false, 
              error: streamError.message || 'Streaming analysis failed' 
            });
          }
          break;
      }

      toast.success('Analysis complete');
    } catch (error) {
      console.error('Analysis error:', error);
      
      // Handle specific error types
      const errorMessage = error.response?.data?.error || error.message || 'Analysis failed';
      const statusCode = error.response?.status;
      
      if (statusCode === 401 || statusCode === 403) {
        toast.error('Invalid or expired API key. Please check your API key.');
        setResult({ 
          success: false, 
          error: 'Authentication failed: Invalid or expired API key',
          statusCode: statusCode
        });
      } else if (statusCode === 429) {
        toast.error('Rate limit exceeded. Please try again later.');
        setResult({ 
          success: false, 
          error: 'Rate limit exceeded. Please wait before making more requests.',
          statusCode: statusCode
        });
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again.');
        setResult({ 
          success: false, 
          error: errorMessage,
          statusCode: statusCode
        });
      } else {
        toast.error(errorMessage);
        setResult({ 
          success: false, 
          error: errorMessage,
          statusCode: statusCode
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getCodeSnippet = () => {
    const baseUrl = 'https://nsfwbackend.techycsr.dev';
    const key = apiKey || 'your_api_key_here';
    
    const snippets = {
      single: {
        curl: inputType === 'url' 
          ? `curl -X POST "${baseUrl}/api/analyze/single" \\\\
  -H "X-API-Key: ${key}" \\\\
  -F "url=${imageUrl || 'https://example.com/image.jpg'}"`
          : `curl -X POST "${baseUrl}/api/analyze/single" \\\\
  -H "X-API-Key: ${key}" \\\\
  -F "file=@/path/to/image.jpg"`,

        javascript: inputType === 'url'
          ? `const formData = new FormData();
formData.append('url', '${imageUrl || 'https://example.com/image.jpg'}');

const response = await fetch('${baseUrl}/api/analyze/single', {
  method: 'POST',
  headers: { 'X-API-Key': '${key}' },
  body: formData
});

const data = await response.json();
console.log(data);`
          : `const formData = new FormData();
formData.append('file', imageFile);

const response = await fetch('${baseUrl}/api/analyze/single', {
  method: 'POST',
  headers: { 'X-API-Key': '${key}' },
  body: formData
});

const data = await response.json();
console.log(data);`,

        python: inputType === 'url'
          ? `import requests

response = requests.post(
    '${baseUrl}/api/analyze/single',
    headers={'X-API-Key': '${key}'},
    data={'url': '${imageUrl || 'https://example.com/image.jpg'}'}
)

print(response.json())`
          : `import requests

with open('image.jpg', 'rb') as f:
    response = requests.post(
        '${baseUrl}/api/analyze/single',
        headers={'X-API-Key': '${key}'},
        files={'file': f}
    )

print(response.json())`,

        node: `const axios = require('axios');
const FormData = require('form-data');
${inputType === 'file' ? "const fs = require('fs');" : ''}

const formData = new FormData();
${inputType === 'url'
  ? `formData.append('url', '${imageUrl || 'https://example.com/image.jpg'}');`
  : `formData.append('file', fs.createReadStream('image.jpg'));`
}

axios.post('${baseUrl}/api/analyze/single', formData, {
  headers: {
    'X-API-Key': '${key}',
    ...formData.getHeaders()
  }
})
.then(res => console.log(res.data))
.catch(err => console.error(err.response.data));`
      },
      multi: {
        curl: `curl -X POST "${baseUrl}/api/analyze/multi" \\\\
  -H "X-API-Key: ${key}" \\\\
  -F "files=@image1.jpg" \\\\
  -F "files=@image2.jpg" \\\\
  -F "files=@image3.jpg"`,

        javascript: `const formData = new FormData();
// Append each file with the key 'files'
files.forEach(file => formData.append('files', file));

const response = await fetch('${baseUrl}/api/analyze/multi', {
  method: 'POST',
  headers: { 'X-API-Key': '${key}' },
  body: formData
});

const data = await response.json();
console.log(data);`,

        python: `import requests

files = [
    ('files', ('image1.jpg', open('image1.jpg', 'rb'), 'image/jpeg')),
    ('files', ('image2.jpg', open('image2.jpg', 'rb'), 'image/jpeg')),
    ('files', ('image3.jpg', open('image3.jpg', 'rb'), 'image/jpeg'))
]

response = requests.post(
    '${baseUrl}/api/analyze/multi',
    headers={'X-API-Key': '${key}'},
    files=files
)

print(response.json())`,

        node: `const axios = require('axios');
const FormData = require('form-data');
const fs = require('fs');

const formData = new FormData();
['image1.jpg', 'image2.jpg', 'image3.jpg'].forEach(img => {
  formData.append('files', fs.createReadStream(img));
});

axios.post('${baseUrl}/api/analyze/multi', formData, {
  headers: {
    'X-API-Key': '${key}',
    ...formData.getHeaders()
  }
})
.then(res => console.log(res.data))
.catch(err => console.error(err.response.data));`
      },
      urls: {
        curl: `curl -X POST "${baseUrl}/api/analyze/urls" \\\\
  -H "X-API-Key: ${key}" \\\\
  -H "Content-Type: application/json" \\\\
  -d '{"urls": [${imageUrls.filter(u => u).map(u => `"${u}"`).join(', ') || '"https://example.com/image1.jpg", "https://example.com/image2.jpg"'}]}'`,

        javascript: `const response = await fetch('${baseUrl}/api/analyze/urls', {
  method: 'POST',
  headers: {
    'X-API-Key': '${key}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    urls: [${imageUrls.filter(u => u).map(u => `'${u}'`).join(', ') || "'https://example.com/image1.jpg', 'https://example.com/image2.jpg'"}]
  })
});

const data = await response.json();
console.log(data);`,

        python: `import requests

response = requests.post(
    '${baseUrl}/api/analyze/urls',
    headers={
        'X-API-Key': '${key}',
        'Content-Type': 'application/json'
    },
    json={
        'urls': [${imageUrls.filter(u => u).map(u => `'${u}'`).join(', ') || "'https://example.com/image1.jpg', 'https://example.com/image2.jpg'"}]
    }
)

print(response.json())`,

        node: `const axios = require('axios');

axios.post('${baseUrl}/api/analyze/urls', {
  urls: [${imageUrls.filter(u => u).map(u => `'${u}'`).join(', ') || "'https://example.com/image1.jpg', 'https://example.com/image2.jpg'"}]
}, {
  headers: {
    'X-API-Key': '${key}',
    'Content-Type': 'application/json'
  }
})
.then(res => console.log(res.data))
.catch(err => console.error(err.response.data));`
      },
      streaming: {
        curl: `curl -X POST "${baseUrl}/api/analyze/streaming" \\\\
  -H "X-API-Key: ${key}" \\\\
  -F "files=@image1.jpg" \\\\
  -F "files=@image2.jpg" \\\\
  --no-buffer`,

        javascript: `const formData = new FormData();
files.forEach(file => formData.append('files', file));

const response = await fetch('${baseUrl}/api/analyze/streaming', {
  method: 'POST',
  headers: { 'X-API-Key': '${key}' },
  body: formData
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  
  const text = decoder.decode(value);
  text.split('\\\\n').forEach(line => {
    if (line.startsWith('data: ')) {
      const data = JSON.parse(line.substring(6));
      console.log('Result:', data);
    }
  });
}`,

        python: `import requests
import json

files = [
    ('files', ('image1.jpg', open('image1.jpg', 'rb'), 'image/jpeg')),
    ('files', ('image2.jpg', open('image2.jpg', 'rb'), 'image/jpeg'))
]

response = requests.post(
    '${baseUrl}/api/analyze/streaming',
    headers={'X-API-Key': '${key}'},
    files=files,
    stream=True
)

for line in response.iter_lines():
    if line:
        data = line.decode('utf-8')
        if data.startswith('data: '):
            result = json.loads(data[6:])
            print(f"Result: {result}")`,

        node: `const axios = require('axios');
const FormData = require('form-data');
const fs = require('fs');

const formData = new FormData();
formData.append('files', fs.createReadStream('image1.jpg'));
formData.append('files', fs.createReadStream('image2.jpg'));

axios.post('${baseUrl}/api/analyze/streaming', formData, {
  headers: {
    'X-API-Key': '${key}',
    ...formData.getHeaders()
  },
  responseType: 'stream'
})
.then(response => {
  response.data.on('data', chunk => {
    const lines = chunk.toString().split('\\\\n');
    lines.forEach(line => {
      if (line.startsWith('data: ')) {
        const data = JSON.parse(line.substring(6));
        console.log('Result:', data);
      }
    });
  });
});`
      }
    };

    return snippets[endpoint]?.[language] || snippets.single.curl;
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(getCodeSnippet());
    toast.success('Code copied to clipboard');
  };

  const clearResults = () => {
    setResult(null);
    setStreamingResults([]);
  };

  const renderInputSection = () => {
    if (endpoint === 'urls') {
      return (
        <div>
          <label className="block text-xs md:text-sm font-medium mb-2">Image URLs (max 10)</label>
          <div className="space-y-2">
            {imageUrls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => updateUrl(index, e.target.value)}
                  placeholder={`URL ${index + 1}`}
                  className="input flex-1 text-sm"
                />
                {imageUrls.length > 1 && (
                  <button
                    onClick={() => removeUrlField(index)}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X className="w-4 md:w-5 h-4 md:h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {imageUrls.length < 10 && (
            <button
              onClick={addUrlField}
              className="mt-2 text-xs md:text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add another URL
            </button>
          )}
        </div>
      );
    }

    if (endpoint === 'single') {
      return (
        <>
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setInputType('url')}
              className={`flex-1 py-2 px-3 md:px-4 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${
                inputType === 'url'
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'bg-gray-200 dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-dark-700'
              }`}
            >
              <LinkIcon className="w-4 h-4 inline mr-2" />
              URL
            </button>
            <button
              onClick={() => setInputType('file')}
              className={`flex-1 py-2 px-3 md:px-4 rounded-lg text-sm md:text-base font-medium transition-all duration-200 ${
                inputType === 'file'
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'bg-gray-200 dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-dark-700'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload
            </button>
          </div>

          {inputType === 'url' ? (
            <div>
              <label className="block text-xs md:text-sm font-medium mb-2">Image URL</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="input text-sm md:text-base"
              />
            </div>
          ) : (
            <div>
              <label className="block text-xs md:text-sm font-medium mb-2">Select Image</label>
              <div 
                className="border-2 border-dashed border-gray-300 dark:border-dark-700 rounded-lg p-6 md:p-8 text-center cursor-pointer hover:border-primary-500 transition-all duration-200 hover:bg-primary-50 dark:hover:bg-primary-900/10"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="w-10 md:w-12 h-10 md:h-12 text-gray-400 mx-auto mb-3" />
                {selectedFiles.length > 0 ? (
                  <div>
                    <p className="text-sm font-medium truncate px-4">{selectedFiles[0].name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(selectedFiles[0].size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium mb-1">Click to upload</p>
                    <p className="text-xs text-gray-500">JPEG, PNG, WebP, GIF up to 50MB</p>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      );
    }

    // Multi and Streaming - file upload only
    const maxFiles = endpoint === 'streaming' ? 100 : 10;
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          Select Images (max {maxFiles})
        </label>
        <div 
          className="border-2 border-dashed border-gray-300 dark:border-dark-700 rounded-lg p-8 text-center cursor-pointer hover:border-primary-500 transition-all duration-200 hover:bg-primary-50 dark:hover:bg-primary-900/10"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-medium mb-1">Click to upload multiple images</p>
          <p className="text-xs text-gray-500">JPEG, PNG, WebP, GIF, AVIF, BMP, TIFF</p>
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{selectedFiles.length} file(s) selected</span>
              <button
                onClick={() => setSelectedFiles([])}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Clear all
              </button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-dark-800 rounded-lg p-2">
                  <div className="flex items-center flex-1 min-w-0">
                    <Image className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-sm truncate">{file.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render status badge for result header
  const renderStatusBadge = (decision) => {
    switch (decision) {
      case 'nsfw':
        return (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full text-sm font-medium">
            <ShieldAlert className="w-4 h-4" />
            NSFW DETECTED
          </div>
        );
      case 'safe':
        return (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full text-sm font-medium">
            <ShieldCheck className="w-4 h-4" />
            SAFE
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full text-sm font-medium">
            <AlertTriangle className="w-4 h-4" />
            ERROR
          </div>
        );
      default:
        return null;
    }
  };

  // Render individual prediction card
  const renderPredictionCard = (prediction, index) => {
    const isNsfw = prediction.final_decision?.toLowerCase() === 'nsfw';
    const confidence = prediction.confidence || prediction.results?.triple_ensemble?.confidence || 0;
    const category = prediction.ensemble_category || prediction.results?.triple_ensemble?.ensemble_prediction?.predicted_category;
    
    return (
      <div
        key={index}
        className={`p-4 rounded-lg border-2 transition-all ${
          isNsfw
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5 text-gray-500" />
            <span className="font-medium truncate max-w-[200px]">
              {prediction.image || prediction.url || `Image ${index + 1}`}
            </span>
          </div>
          <div className={`px-2 py-0.5 rounded text-xs font-bold ${
            isNsfw
              ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
              : 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
          }`}>
            {isNsfw ? 'NSFW' : 'SAFE'}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <Shield className="w-3.5 h-3.5" />
            <span>Confidence:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {(confidence * 100).toFixed(1)}%
            </span>
          </div>
          {category && (
            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
              <Cpu className="w-3.5 h-3.5" />
              <span>Category:</span>
              <span className="font-medium text-gray-900 dark:text-white">{category}</span>
            </div>
          )}
          {prediction.total_time_ms && (
            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
              <Clock className="w-3.5 h-3.5" />
              <span>Time:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {prediction.total_time_ms.toFixed(0)}ms
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResult = () => {
    // Streaming results
    if (endpoint === 'streaming' && streamingResults.length > 0) {
      const summary = streamingResults.find(r => r.type === 'summary');
      const predictions = streamingResults.filter(r => r.type !== 'summary' && r.type !== 'error');
      const errors = streamingResults.filter(r => r.type === 'error');
      
      return (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">Streaming Results</h2>
              {summary && (
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                    {summary.safe_count} Safe
                  </span>
                  <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                    {summary.nsfw_count} NSFW
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={clearResults}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Progress bar */}
          {predictions.length > 0 && predictions[predictions.length - 1]?.progress && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                <span>Progress</span>
                <span>{predictions[predictions.length - 1].progress.current} / {predictions[predictions.length - 1].progress.total}</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary-600 transition-all duration-300"
                  style={{ width: `${(predictions[predictions.length - 1].progress.current / predictions[predictions.length - 1].progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {predictions.map((result, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-lg text-sm ${
                  result.final_decision?.toLowerCase() === 'nsfw'
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-gray-500" />
                    <span className="truncate flex-1">{result.image}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {((result.confidence || 0) * 100).toFixed(1)}%
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      result.final_decision?.toLowerCase() === 'nsfw' 
                        ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                        : 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                    }`}>
                      {result.final_decision}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {errors.length > 0 && errors.map((err, index) => (
              <div key={`err-${index}`} className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">{err.error || err.image}</span>
                </div>
              </div>
            ))}
            
            {summary && (
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 mt-4">
                <p className="font-medium mb-2 text-blue-800 dark:text-blue-300">Summary</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Total: <span className="font-medium">{summary.total_processed}</span></div>
                  <div>Safe: <span className="font-medium text-green-600">{summary.safe_count}</span></div>
                  <div>NSFW: <span className="font-medium text-red-600">{summary.nsfw_count}</span></div>
                  <div>Time: <span className="font-medium">{(summary.total_time_ms / 1000).toFixed(2)}s</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (!result) return null;

    const decision = getDecision(result);

    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold">Result</h2>
            {renderStatusBadge(decision)}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRawResponse(!showRawResponse)}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
              title={showRawResponse ? "Show formatted" : "Show raw JSON"}
            >
              <Eye className="w-5 h-5" />
            </button>
            <button
              onClick={clearResults}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {result.error || !result.success ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 dark:text-red-300 font-medium">
                  {result.statusCode === 401 || result.statusCode === 403 
                    ? 'Authentication Error' 
                    : result.statusCode === 429 
                    ? 'Rate Limit Exceeded'
                    : 'Error'}
                </p>
                <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                  {typeof result.error === 'string' ? result.error : result.error?.error || result.error?.message || 'Request failed'}
                </p>
                {result.statusCode && (
                  <p className="text-red-500 dark:text-red-500 text-xs mt-2">
                    Status Code: {result.statusCode}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : showRawResponse ? (
          <div className="bg-gray-50 dark:bg-dark-800 rounded-lg p-4 max-h-96 overflow-auto">
            <pre className="text-sm whitespace-pre-wrap font-mono">
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Single result */}
            {result.data && !result.data.predictions && (
              <div className={`p-4 rounded-lg border-2 ${
                decision === 'nsfw'
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              }`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Decision</p>
                    <p className={`text-lg font-bold ${decision === 'nsfw' ? 'text-red-600' : 'text-green-600'}`}>
                      {result.data.final_decision || (decision === 'nsfw' ? 'NSFW' : 'SAFE')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Confidence</p>
                    <p className="text-lg font-bold">
                      {((result.data.confidence || 0) * 100).toFixed(1)}%
                    </p>
                  </div>
                  {result.data.ensemble_category && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Category</p>
                      <p className="text-lg font-medium">{result.data.ensemble_category}</p>
                    </div>
                  )}
                  {result.data.total_time_ms && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Processing Time</p>
                      <p className="text-lg font-medium">{result.data.total_time_ms.toFixed(0)}ms</p>
                    </div>
                  )}
                </div>
                
                {result.data.models_used && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-600">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Models Used</p>
                    <div className="flex flex-wrap gap-2">
                      {result.data.models_used.map((model, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-200 dark:bg-dark-700 rounded text-xs font-medium">
                          {model}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Multi/URLs results */}
            {result.data?.predictions && (
              <div className="space-y-3">
                {/* Summary */}
                {result.data.summary && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{result.data.summary.total_images}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{result.data.summary.safe_images}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Safe</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600">{result.data.summary.nsfw_images}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">NSFW</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{(result.data.summary.total_time_ms / 1000).toFixed(2)}s</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Time</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Individual predictions */}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {result.data.predictions.map((pred, index) => renderPredictionCard(pred, index))}
                </div>
              </div>
            )}
            
            {/* Metadata */}
            {result.metadata && (
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-4">
                <span>Request ID: {result.metadata.requestId}</span>
                {result.metadata.processingTime && (
                  <span>Total: {result.metadata.processingTime}ms</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6 px-4 md:px-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">API Playground</h1>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
            Test the NSFW Detection API with real images using your API key
          </p>
        </div>
        {(result || streamingResults.length > 0) && (
          <button
            onClick={clearResults}
            className="btn-secondary flex items-center justify-center gap-2 w-full md:w-auto flex-shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Clear Results</span>
          </button>
        )}
      </div>

      {/* Endpoint Selector */}
      <div className="card">
        <h2 className="text-lg md:text-xl font-semibold mb-4">Select Endpoint</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {endpoints.map((ep) => (
            <button
              key={ep.id}
              onClick={() => {
                setEndpoint(ep.id);
                setSelectedFiles([]);
                setResult(null);
                setStreamingResults([]);
                if (ep.id !== 'single') {
                  setInputType('file');
                }
              }}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                endpoint === ep.id
                  ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 shadow-md'
                  : 'border-gray-200 dark:border-dark-700 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-dark-800'
              }`}
            >
              <ep.icon className={`w-6 h-6 mx-auto mb-2 transition-colors ${
                endpoint === ep.id ? 'text-primary-600' : 'text-gray-400'
              }`} />
              <p className={`text-sm font-medium ${endpoint === ep.id ? 'text-primary-600' : ''}`}>
                {ep.name}
              </p>
              <p className="text-xs text-gray-500 mt-1">{ep.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
        {/* Left Panel - Input */}
        <div className="space-y-4 md:space-y-6">
          {/* API Key */}
          <div className="card">
            <h2 className="text-lg md:text-xl font-semibold mb-4">Authentication</h2>
            <div>
              <label className="block text-sm font-medium mb-2">API Key</label>
              <div className="relative">
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="nsfw_xxxxxxxx..."
                  className={`input pr-10 text-sm md:text-base ${apiKey && apiKey.length < 20 ? 'border-yellow-500 focus:ring-yellow-500' : ''}`}
                />
                {apiKey && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {apiKey.length >= 20 ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    )}
                  </div>
                )}
              </div>
              {apiKey && apiKey.length < 20 && (
                <p className="text-xs md:text-sm text-yellow-600 mt-1">
                  API key seems too short. Please check your API key.
                </p>
              )}
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                Get your API key from the{' '}
                <a href="/dashboard/keys" className="text-primary-600 hover:underline font-medium">
                  API Keys page
                </a>
              </p>
            </div>
          </div>

          {/* Input */}
          <div className="card">
            <h2 className="text-lg md:text-xl font-semibold mb-4">Input</h2>
            {renderInputSection()}

            <button
              onClick={handleAnalyze}
              disabled={loading || !apiKey}
              className="btn-primary w-full mt-4 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play className="w-4 md:w-5 h-4 md:h-5" />
                  <span>Analyze {endpoint === 'single' ? 'Image' : 'Images'}</span>
                </>
              )}
            </button>
          </div>

          {/* Result */}
          {renderResult()}
        </div>

        {/* Right Panel - Code */}
        <div className="space-y-4 md:space-y-6">
          <div className="card">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
              <h2 className="text-lg md:text-xl font-semibold">Code Example</h2>
              <button
                onClick={handleCopyCode}
                className="btn-secondary flex items-center justify-center space-x-2 w-full md:w-auto"
              >
                <Copy className="w-4 h-4" />
                <span>Copy</span>
              </button>
            </div>

            {/* Language Selector */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['curl', 'javascript', 'python', 'node'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 ${
                    language === lang
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-200 dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-dark-700'
                  }`}
                >
                  {lang === 'curl' ? 'cURL' : lang === 'node' ? 'Node.js' : lang.charAt(0).toUpperCase() + lang.slice(1)}
                </button>
              ))}
            </div>

            {/* Code Block */}
            <div className="bg-dark-900 rounded-lg overflow-hidden">
              <Highlight
                theme={themes.nightOwl}
                code={getCodeSnippet()}
                language={language === 'node' ? 'javascript' : language === 'curl' ? 'bash' : language}
              >
                {({ className, style, tokens, getLineProps, getTokenProps }) => (
                  <pre className={`${className} p-2 md:p-4 overflow-x-auto text-xs md:text-sm`} style={style}>
                    {tokens.map((line, i) => (
                      <div key={i} {...getLineProps({ line })}>
                        {line.map((token, key) => (
                          <span key={key} {...getTokenProps({ token })} />
                        ))}
                      </div>
                    ))}
                  </pre>
                )}
              </Highlight>
            </div>
          </div>

          {/* Endpoint Info */}
          <div className="card bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-primary-200 dark:border-primary-800">
            <h3 className="text-sm md:text-base font-semibold mb-3 flex items-center gap-2">
              {(() => {
                const EndpointIcon = endpoints.find(e => e.id === endpoint)?.icon || Image;
                return <EndpointIcon className="w-4 md:w-5 h-4 md:h-5 text-primary-600" />;
              })()}
              {endpoints.find(e => e.id === endpoint)?.name} Endpoint
            </h3>
            <div className="text-xs md:text-sm space-y-2 text-gray-600 dark:text-gray-400">
              {endpoint === 'single' && (
                <>
                  <p><code className="bg-gray-200 dark:bg-dark-700 px-1.5 py-0.5 rounded">POST</code> /api/analyze/single</p>
                  <p>Analyze a single image via file upload or URL.</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Max file size: 50MB</li>
                    <li>Supports: JPEG, PNG, WebP, GIF, AVIF, BMP, TIFF</li>
                    <li>Returns detailed ensemble prediction</li>
                  </ul>
                </>
              )}
              {endpoint === 'multi' && (
                <>
                  <p><code className="bg-gray-200 dark:bg-dark-700 px-1.5 py-0.5 rounded">POST</code> /api/analyze/multi</p>
                  <p>Batch analyze multiple images at once.</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Max files: 10 per request</li>
                    <li>Max total size: 500MB</li>
                    <li>Returns predictions for all images + summary</li>
                  </ul>
                </>
              )}
              {endpoint === 'urls' && (
                <>
                  <p><code className="bg-gray-200 dark:bg-dark-700 px-1.5 py-0.5 rounded">POST</code> /api/analyze/urls</p>
                  <p>Analyze images from URLs.</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Max URLs: 10 per request</li>
                    <li>Content-Type: application/json</li>
                    <li>30s timeout per URL download</li>
                  </ul>
                </>
              )}
              {endpoint === 'streaming' && (
                <>
                  <p><code className="bg-gray-200 dark:bg-dark-700 px-1.5 py-0.5 rounded">POST</code> /api/analyze/streaming</p>
                  <p>Real-time streaming for large batches.</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Max files: 100 per request (2500 on main model)</li>
                    <li>Returns Server-Sent Events (SSE)</li>
                    <li>Get results as they're processed</li>
                  </ul>
                </>
              )}
            </div>
          </div>

          {/* Model Info */}
          <div className="card bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-800">
            <h3 className="text-sm md:text-base font-semibold mb-3 flex items-center gap-2">
              <Cpu className="w-4 md:w-5 h-4 md:h-5 text-purple-600" />
              <span className="truncate">Triple-Model Ensemble</span>
            </h3>
            <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p>This API uses state-of-the-art Vision Transformers:</p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div className="bg-white dark:bg-dark-800 rounded p-2 text-center">
                  <p className="text-xs md:text-sm font-bold text-purple-600">ViT-L-14</p>
                  <p className="text-xs">40% weight</p>
                </div>
                <div className="bg-white dark:bg-dark-800 rounded p-2 text-center">
                  <p className="text-xs md:text-sm font-bold text-purple-600">ViT-H-14</p>
                  <p className="text-xs">35% weight</p>
                </div>
                <div className="bg-white dark:bg-dark-800 rounded p-2 text-center">
                  <p className="text-xs md:text-sm font-bold text-purple-600">ViT-g-14</p>
                  <p className="text-xs">25% weight</p>
                </div>
              </div>
            </div>
          </div>

          {/* Documentation Link */}
          <div className="card bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <div className="flex items-start space-x-3">
              <Code className="w-5 md:w-6 h-5 md:h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-sm md:text-base font-semibold mb-1">Need Help?</h3>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Check out our comprehensive documentation for more examples and best practices.
                </p>
                <a href="/docs" className="text-primary-600 dark:text-primary-400 text-xs md:text-sm font-medium hover:underline inline-flex items-center gap-1">
                  View Documentation →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiPlayground;
